import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import "./PaymentResult.css";

const SETTLED = ["paid", "failed", "expired", "refunded"];
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 10;
// After a failed attempt there is nothing to wait for unless the member pays
// in another tab, so the page checks a couple of times and stops.
const MAX_POLLS_AFTER_FAILED_ATTEMPT = 2;

/**
 * Where Whish returns the member after payment.
 *
 * The status endpoint re-checks with Whish on every call, so a member who gets
 * back before the server-to-server callback still leaves this page with their
 * access granted rather than being told to wait.
 *
 * Whish sends the member here with `attempt=failed` when an attempt did not go
 * through. That does not end the payment: the link stays payable and Whish
 * keeps reporting it as pending, so the member is offered the same link again
 * rather than told it is still processing.
 */
const PaymentResult = () => {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const reference = searchParams.get("ref");
  const attemptFailed = searchParams.get("attempt") === "failed";

  const [transaction, setTransaction] = useState(null);
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  const check = useCallback(async () => {
    if (!reference) {
      setState("missing");
      return true;
    }

    try {
      const { data } = await apiClient.get(`/payments/${reference}/status`);
      const payment = data.data ?? null;
      setTransaction(payment);

      if (payment && SETTLED.includes(payment.status)) {
        setState({ paid: "paid", refunded: "refunded" }[payment.status] || "failed");
        return true;
      }

      setState(attemptFailed && payment?.collect_url ? "retry" : "pending");
      return false;
    } catch (error) {
      setState(error.response?.status === 404 ? "missing" : "error");
      return true;
    }
  }, [reference, attemptFailed]);

  useEffect(() => {
    if (authLoading || !user) return undefined;

    let cancelled = false;
    let timer;

    const run = async () => {
      const done = await check();
      if (cancelled) return;

      attempts.current += 1;

      const maxPolls = attemptFailed ? MAX_POLLS_AFTER_FAILED_ATTEMPT : MAX_POLLS;

      if (!done && attempts.current < maxPolls) {
        timer = window.setTimeout(run, POLL_INTERVAL_MS);
      }
    };

    run();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [authLoading, user, check, attemptFailed]);

  const amount = transaction ? `${transaction.currency} ${transaction.amount}` : null;

  const view = {
    checking: {
      icon: "fa-solid fa-circle-notch fa-spin",
      tone: "is-pending",
      title: t("Confirming your payment..."),
      body: t("This only takes a moment."),
    },
    pending: {
      icon: "fa-solid fa-clock",
      tone: "is-pending",
      title: t("Your payment is still processing"),
      body: t("We have not had final confirmation from Whish yet. You can safely close this page. Your access opens automatically as soon as the payment clears."),
    },
    paid: {
      icon: "fa-solid fa-circle-check",
      tone: "is-paid",
      title: t("Payment confirmed"),
      body: t("Your subscription is active. Everything it unlocks is ready for you now."),
    },
    retry: {
      icon: "fa-solid fa-rotate-right",
      tone: "is-failed",
      title: t("That payment attempt did not go through"),
      body: t("Nothing was charged. Your payment link is still open, so you can try again now."),
    },
    failed: {
      icon: "fa-solid fa-circle-xmark",
      tone: "is-failed",
      title: t("Payment was not completed"),
      body: t("The payment link expired before it was paid. Nothing was charged. You can pick your package again whenever you are ready."),
    },
    refunded: {
      icon: "fa-solid fa-rotate-left",
      tone: "is-pending",
      title: t("This payment was refunded"),
      body: t("The amount was returned to your Whish balance and the subscription it paid for has ended. Contact the academy team if this was not expected."),
    },
    missing: {
      icon: "fa-solid fa-circle-question",
      tone: "is-failed",
      title: t("We could not find that payment"),
      body: t("Check the link you followed, or pick your package again."),
    },
    error: {
      icon: "fa-solid fa-triangle-exclamation",
      tone: "is-failed",
      title: t("Could not check your payment"),
      body: t("Please refresh in a moment. If it keeps happening, contact the academy team."),
    },
  }[state];

  if (!authLoading && !user) {
    return (
      <div className="page-content hpa-payment-page">
        <div className="container">
          <div className="hpa-payment-card is-pending">
            <span className="hpa-payment-icon"><i className="fa-solid fa-lock" /></span>
            <h1>{t("Login Required")}</h1>
            <p>{t("Log in to see the result of your payment.")}</p>
            <Link to="/auth" className="hpa-payment-btn">{t("Login")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content hpa-payment-page">
      <div className="container">
        <div className={`hpa-payment-card ${view.tone}`} role="status" aria-live="polite">
          <span className="hpa-payment-icon"><i className={view.icon} /></span>
          <h1>{view.title}</h1>
          <p>{view.body}</p>

          {amount && (
            <dl className="hpa-payment-meta">
              <div><dt>{t("Amount")}</dt><dd>{amount}</dd></div>
              <div><dt>{t("Reference")}</dt><dd>{transaction.external_id}</dd></div>
            </dl>
          )}

          <div className="hpa-payment-actions">
            {state === "paid" && (
              <>
                <Link to="/libraries" className="hpa-payment-btn">{t("Start training")}</Link>
                <Link to="/my-program" className="hpa-payment-btn is-ghost">{t("My Program")}</Link>
              </>
            )}
            {state === "retry" && (
              <>
                <a href={transaction.collect_url} className="hpa-payment-btn">{t("Try again")}</a>
                <Link to="/packages" className="hpa-payment-btn is-ghost">{t("Back to packages")}</Link>
              </>
            )}
            {(state === "failed" || state === "missing" || state === "refunded") && (
              <Link to="/packages" className="hpa-payment-btn">{t("Back to packages")}</Link>
            )}
            {state === "pending" && (
              <Link to="/libraries" className="hpa-payment-btn is-ghost">{t("Explore the libraries")}</Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
